import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { antiScrapingOptimized } from '../../utils/antiScrapingOptimized';

interface ProtectedDataProps {
  children: React.ReactNode;
  delay?: number;
  obfuscatedData?: string;
  className?: string;
  showAlternative?: boolean;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ProtectedContainer = styled.div<{ $isProtected: boolean }>`
  position: relative;
  display: inline-block;
  cursor: ${props => props.$isProtected ? 'not-allowed' : 'auto'};
  user-select: ${props => props.$isProtected ? 'none' : 'auto'};
  -webkit-user-select: ${props => props.$isProtected ? 'none' : 'auto'};
  -moz-user-select: ${props => props.$isProtected ? 'none' : 'auto'};
  -ms-user-select: ${props => props.$isProtected ? 'none' : 'auto'};
  
  &::selection {
    background: transparent;
  }
  &::-moz-selection {
    background: transparent;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  border-radius: ${props => props.theme.borderRadius.md};
  z-index: 1;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.theme.colors.primary[200]};
  border-top: 2px solid ${props => props.theme.colors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const ProtectedData: React.FC<ProtectedDataProps> = ({
  children,
  delay = 100,
  obfuscatedData,
  className,
  showAlternative = true
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isBot, setIsBot] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Vérifier si c'est un bot
    const botDetection = antiScrapingOptimized.detectBot();
    setIsBot(botDetection.isBot);

    if (botDetection.isBot) {
      console.warn('🚫 Bot detected, blocking sensitive data');
      if (showAlternative) {
        setShowContent(true);
      }
      return;
    }

    // Délai avant d'afficher le contenu
    const timer = setTimeout(() => {
      setIsLoaded(true);
      setShowContent(true);
      
      // Protéger l'élément une fois chargé
      if (containerRef.current) {
        antiScrapingOptimized.protectElement(containerRef.current);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, showAlternative]);

  const handleClick = (e: React.MouseEvent) => {
    if (!isLoaded) {
      e.preventDefault();
      antiScrapingOptimized.showWarning('⏳ Contenu en cours de chargement...');
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    antiScrapingOptimized.showWarning('⚠️ Clic droit désactivé sur ce contenu.');
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    antiScrapingOptimized.showWarning('⚠️ Glisser-déposer désactivé pour ce contenu.');
  };

  if (isBot && !showAlternative) {
    return null; // Masquer complètement pour les bots
  }

  if (isBot && showAlternative) {
    return (
          <ProtectedContainer 
            ref={containerRef}
            className={className}
            $isProtected={true}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
      >
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: '#666',
          fontSize: '14px'
        }}>
          <p>🔒 Contenu protégé</p>
          <p>Pour accéder à ces informations, contactez-nous directement.</p>
        </div>
      </ProtectedContainer>
    );
  }

  return (
        <ProtectedContainer 
          ref={containerRef}
          className={className}
          $isProtected={!isLoaded}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
    >
      {!isLoaded && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
      
      {showContent && (
        <div style={{ opacity: isLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}>
          {obfuscatedData ? antiScrapingOptimized.deobfuscate(obfuscatedData) : children}
        </div>
      )}
    </ProtectedContainer>
  );
};

export default ProtectedData;

